import React from "react";
import { createRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";

import App from "./App";

import i18n from "./i18n";
import "./utils/suppress-console";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import {
  beforeSendWithQuota,
  beforeSendTransactionWithQuota,
  getReplaySampleRate,
  trackReplayCreated,
} from "./utils/sentry-quota";
import { analyticsManager } from "./utils/analytics";
import { onAuthStateChanged, getAuth } from "firebase/auth";
import { firebaseApp } from "./utils/auth/firebaseConfig";
import { trackReferralSource, trackUTMParameters, trackDeviceDetails } from "./utils/analytics-events";

// Initialize Sentry before anything else
const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
const sentryEnvironment = import.meta.env.VITE_SENTRY_ENVIRONMENT || "development";
const sentryEnabled = Boolean(sentryDsn) && import.meta.env.VITE_SENTRY_ENVIRONMENT === "production";

if (sentryEnabled) {
  Sentry.init({
    dsn: sentryDsn,
    environment: sentryEnvironment,
    release: import.meta.env.VITE_SENTRY_RELEASE,
    
    // Integrations
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Session replay configuration
        maskAllText: false,
        blockAllMedia: false,
        maskAllInputs: true,
      }),
      Sentry.captureConsoleIntegration({
        levels: ["error", "assert"],
      }),
    ],
    
    // Performance Monitoring - 100% sample rate for B2B (low traffic)
    tracesSampleRate: 1.0,
    
    // Session Replay sample rates
    replaysSessionSampleRate: getReplaySampleRate(0.1), // 10% of sessions
    replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors
    
    // Quota management hooks
    beforeSend: beforeSendWithQuota,
    beforeSendTransaction: beforeSendTransactionWithQuota,
    
    // Debug mode (from environment)
    debug: import.meta.env.VITE_ANALYTICS_DEBUG === "true",
  });
}

dayjs.extend(relativeTime);

// Initialize Google Analytics and GTM with Firebase auth listener
const initializeAnalytics = () => {
  if (import.meta.env.VITE_ENABLE_ANALYTICS !== 'false') {
    try {
      // Track attribution sources (referral, UTM, device details)
      trackReferralSource();
      trackUTMParameters();
      trackDeviceDetails();
      
      // Listen to Firebase auth state changes and set up GA user tracking
      const auth = getAuth(firebaseApp);
      onAuthStateChanged(auth, (user) => {
        if (user) {
          // User is signed in - set GA user properties
          analyticsManager.setUserProperties(user.uid, {
            user_type: 'authenticated',
            user_email: user.email,
            firebase_uid: user.uid,
          });
          
          if (analyticsManager.getDebugMode()) {
            console.log(`✅ Analytics user identified: ${user.uid}`);
          }
        } else {
          // User is signed out
          if (analyticsManager.getDebugMode()) {
            console.log('ℹ️ Analytics user cleared (user signed out)');
          }
        }
      });

      // Initialize GTM data layer if available
      if (typeof window !== 'undefined' && import.meta.env.VITE_ENABLE_ANALYTICS === 'true') {
        window.dataLayer = window.dataLayer || [];
        
        // Initial data layer push with environment info
        window.dataLayer.push({
          'gtm.start': new Date().getTime(),
          'event': 'gtm.js',
          'environment': import.meta.env.VITE_ENV,
          'app': 'getlost-admin',
        });
      }

      if (analyticsManager.getDebugMode()) {
        console.log('✅ Google Analytics and GTM initialized');
      }
    } catch (error) {
      console.error('Failed to initialize analytics:', error);
      Sentry.captureException(error, {
        tags: { module: 'analytics_init' }
      });
    }
  }
};

// Initialize analytics
initializeAnalytics();

const container = document.getElementById("root");
// eslint-disable-next-line
const root = createRoot(container!);

const render = () => {
  root.render(
    <React.StrictMode>
      <App />
    </React.StrictMode>,
  );
};

const ensureI18nReady = async () => {
  // Normalize to base language and ensure default namespace is loaded
  const base = (i18n.language || "en").split("-")[0];
  if (i18n.language !== base) {
    await i18n.changeLanguage(base);
  }
  await i18n.loadNamespaces(["translation"]);
};

// Wait for i18n to load the default namespace to avoid missing-key logs
ensureI18nReady().then(render).catch(render);
